import React, { FC, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ProjectPageShell, { Crumb } from "../shared/ProjectPageShell";
import PhaseInDetailView from "./PhaseInDetailsView";
import { RootState } from "../../../store/store";
import { updatePhaseInDetailModalSlice } from "../../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import { phaseApis } from "../../../api/Phase";
import { useWbsName } from "../shared/useWbsCodes";

const PhaseDetailPage: FC = () => {
  const { phaseId } = useParams<{ phaseId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wbsName = useWbsName();

  const [loading, setLoading] = useState(true);
  const [phaseData, setPhaseData] = useState<any>(null);

  // Get data from location state or Redux
  const locationState = location.state as any;
  const projectId = locationState?.projectId || locationState?.id;
  const projectName = locationState?.projectName;
  // Generic return path - allows navigation back to any source page
  const returnPath = locationState?.returnPath;
  const returnState = locationState?.returnState;

  const { data: PhaseInDetailModal } = useSelector(
    (state: RootState) => state.PhaseInDetailModal
  );

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const resolvedProjectId = projectId || currentProjectId;
  const resolvedProjectName = projectName || currentProjectName;

  // Fetch phase details
  useEffect(() => {
    const fetchPhaseDetails = async () => {
      if (!phaseId) return;

      setLoading(true);
      try {
        const response = await phaseApis.getPhaseById(Number(phaseId));
        if (response?.status && response?.data) {
          const phase = response.data;
          setPhaseData(phase);

          // Update Redux state for child components
          dispatch(
            updatePhaseInDetailModalSlice({
              open: true,
              id: phase.phaseId,
              phaseName: phase.phaseName,
              activeTab: "activities",
              allowEdit: true,
              allowAdd: true,
              projectResponse: {
                id: phase.projectId || resolvedProjectId,
                name: resolvedProjectName || phase.projectName || "Project",
                status: phase.projectStatus || "Active",
              },
              extra: {
                status: phase.status,
                startDate: phase.phaseStartDate,
                endDate: phase.phaseEndDate,
                ownerName: phase.phaseOwnerName,
                createdAt: phase.createdAt,
                createdByUserName: phase.createdByUserName,
              },
            })
          );
        }
      } catch (error) {
        console.error("Error fetching phase details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPhaseDetails();
  }, [phaseId, dispatch, resolvedProjectId, resolvedProjectName]);

  // Handle back navigation - uses returnPath if available
  const handleBack = () => {
    if (returnPath) {
      navigate(returnPath, { state: returnState });
    } else {
      navigate(`/pms/project/phases/${resolvedProjectId}`, {
        state: locationState,
      });
    }
  };

  // Handle navigation back to phases list
  const handleBackToPhases = () => {
    navigate(`/pms/project/phases/${resolvedProjectId}`, {
      state: locationState,
    });
  };

  // Handle navigation back to project
  const handleBackToProject = () => {
    navigate(`/pms/project/dashboard/${resolvedProjectId}`, {
      state: locationState,
    });
  };

  const rawPhaseName =
    phaseData?.phaseName || PhaseInDetailModal?.phaseName || "Phase";
  const phaseName = wbsName(rawPhaseName);

  const breadcrumb: Crumb[] = [
    { label: resolvedProjectName || "Project", onClick: handleBackToProject },
    { label: "Phases", onClick: handleBackToPhases },
    { label: phaseName },
  ];

  return (
    <ProjectPageShell
      headTitle={`${phaseName} - Phase Details`}
      breadcrumb={breadcrumb}
      title={phaseName}
      eyebrow="Phase"
      onBack={handleBack}
      loading={loading}
    >
      <PhaseInDetailView activeTab="activities" />
    </ProjectPageShell>
  );
};

export default PhaseDetailPage;
